import { useState } from "react";
import { Banknote, Calculator, CheckCircle2, CreditCard, HelpCircle, RotateCcw, Wallet } from "lucide-react";

import { InputDecimal } from "@/components/ui/input-decimal";

import ContagemNotas, { MOEDAS, NOTAS, contarPecas, somaContagem } from "../abertura/ContagemNotas";
import { reais } from "../lib/caixa";

/** Resumo embaixo das notas, como na abertura: peças, Zerar e o total que vai para o Dinheiro. */
function TotalNotas({ qtd, zerar }) {
  const notas = contarPecas(qtd, NOTAS);
  const moedas = contarPecas(qtd, MOEDAS);
  return (
    <div className="flex items-center justify-between gap-3 border-t pt-3">
      <p className="text-[11px] text-muted-foreground flex items-center gap-2" data-e2e="fechamento-pecas">
        <Banknote className="w-3.5 h-3.5" />
        {notas} {notas === 1 ? "nota" : "notas"} · {moedas} {moedas === 1 ? "moeda" : "moedas"}
        {notas + moedas > 0 ? (
          <button type="button" onClick={zerar} className="inline-flex items-center gap-0.5 text-primary font-semibold">
            <RotateCcw className="w-3 h-3" /> Zerar
          </button>
        ) : null}
      </p>
      <span className="text-xl font-black tabular-nums text-primary">{reais(somaContagem(qtd))}</span>
    </div>
  );
}

function LinhaForma({ f, valor, setValor }) {
  const [cedulas, setCedulas] = useState(null);
  // A mesma contagem da abertura (dono, 25/10/2026): a soma vai direto para o campo de Dinheiro
  const trocarCedulas = (mudar) => {
    const prox = typeof mudar === "function" ? mudar(cedulas || {}) : mudar;
    setCedulas(prox);
    setValor(String(somaContagem(prox)));
  };
  return (
    <li className="p-3 sm:p-4" data-e2e="contagem-forma">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">{f.gaveta ? <Wallet className="w-5 h-5" /> : <CreditCard className="w-5 h-5" />}</div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold">{f.forma}</p>
          <p className="text-[11px] text-muted-foreground">{f.gaveta ? "Todo o dinheiro da gaveta, com o troco" : `${f.qtd} venda(s) — some os comprovantes`}</p>
        </div>
        <div className="flex items-center gap-1.5 w-40">
          <span className="text-muted-foreground text-sm">R$</span>
          <InputDecimal decimals={2} value={valor} onChange={setValor} className="h-11 text-right text-lg font-bold" data-e2e={`contado-${f.forma}`} />
        </div>
      </div>
      {f.gaveta && (
        <button type="button" onClick={() => setCedulas(cedulas ? null : {})} className="mt-2 ml-[52px] text-xs text-primary font-medium flex items-center gap-1.5">
          <Calculator className="w-3.5 h-3.5" />{cedulas ? "Fechar contagem de notas" : "Contar notas e moedas"}
        </button>
      )}
      {cedulas && (
        <div className="mt-3 rounded-2xl bg-muted/30 border p-3 space-y-3" data-e2e="fechamento-notas">
          <ContagemNotas qtd={cedulas} setQtd={trocarCedulas} />
          <TotalNotas qtd={cedulas} zerar={() => trocarCedulas({})} />
        </div>
      )}
    </li>
  );
}

/** Passo 1 — contagem às cegas: o operador não vê o esperado antes de contar.
 *  (O aviso amarelo "pedidos deste caixa não concluídos — não entram na conta"
 *  saiu em 22/10/2026: agora pedido em aberto TRAVA o fechamento — AvisoEmAberto.) */
export default function PassoContagem({ f }) {
  const { formas, contado, setContado } = f;
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">Conte o que tem em cada forma e digite abaixo. Você só vê o valor esperado depois de conferir.</p>
      <ul className="rounded-2xl border divide-y">
        {formas.manuais.map((m) => <LinhaForma key={m.forma} f={m} valor={contado[m.forma] ?? ""} setValor={(v) => setContado((c) => ({ ...c, [m.forma]: v }))} />)}
      </ul>
      {formas.semForma.length > 0 && (
        <div className="rounded-xl bg-muted/40 p-3 text-sm flex gap-2">
          <HelpCircle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
          <span><strong>{formas.semForma.map((a) => a.forma_pagamento).join(", ")}</strong>: vendas sem a forma real registrada — não dá para contar separado. O dinheiro delas está em alguma forma acima; se sobrar lá, é isso.</span>
        </div>
      )}
      {formas.automaticas.length > 0 && (
        <div className="rounded-xl bg-muted/40 p-3 text-sm flex gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
          <span><strong>{formas.automaticas.map((a) => a.forma_pagamento).join(", ")}</strong>: pagos online, confirmados pelo sistema — não precisa contar.</span>
        </div>
      )}
    </div>
  );
}
